import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Music, ChevronLeft, Plus, Trash2, MapPin, Calendar as CalIcon,
  Users, Phone, Save, ListMusic, CheckSquare, StickyNote, ExternalLink,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const uid = () => Math.random().toString(36).slice(2, 10);

// Build a Spotify embed URL from any playlist/album/track/artist link or URI.
const spotifyEmbed = (url) => {
  if (!url) return null;
  const s = String(url).trim();
  const uriM = s.match(/spotify:(playlist|album|track|artist|show|episode):([A-Za-z0-9]+)/);
  if (uriM) return `https://open.spotify.com/embed/${uriM[1]}/${uriM[2]}`;
  const m = s.match(/open\.spotify\.com\/(?:intl-[a-z-]+\/)?(playlist|album|track|artist|show|episode)\/([A-Za-z0-9]+)/);
  if (m) return `https://open.spotify.com/embed/${m[1]}/${m[2]}`;
  return null;
};

function EventList({ onOpen }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/dj/events").then(({ data }) => setEvents(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <p className="text-zinc-500" data-testid="dj-loading">Chargement…</p>;
  if (!events.length)
    return (
      <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 p-10 text-center" data-testid="dj-empty">
        <Music className="mx-auto h-9 w-9 text-[#FFD500]" />
        <p className="mt-3 font-display text-lg font-semibold text-white">Aucun événement</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-zinc-500">Créez un événement pour préparer sa fiche DJ (playlists Spotify & checklist jour J).</p>
      </div>
    );
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="dj-event-list">
      {events.map((e) => (
        <button
          key={e.id}
          data-testid={`dj-event-${e.id}`}
          onClick={() => onOpen(e.id)}
          className="group flex flex-col items-start rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 text-left transition-colors hover:border-[#FF6A00]/60 hover:bg-zinc-900"
        >
          <div className="flex w-full items-start justify-between gap-2">
            <span className="font-display text-lg font-bold text-white">{e.title}</span>
            {e.has_sheet && (
              <span className="rounded-full bg-[#FF6A00]/15 px-2 py-0.5 text-[11px] font-medium text-[#FF6A00]" data-testid="dj-badge-ready">Fiche prête</span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-sm text-zinc-400"><CalIcon className="h-3.5 w-3.5" />{fmtDate(e.date)}</div>
          {e.location && <div className="mt-1 flex items-center gap-1.5 text-sm text-zinc-500"><MapPin className="h-3.5 w-3.5" />{e.location}</div>}
          {e.contact_name && <div className="mt-1 flex items-center gap-1.5 text-sm text-zinc-500"><Users className="h-3.5 w-3.5" />{e.contact_name}</div>}
        </button>
      ))}
    </div>
  );
}

function InfoRow({ icon: Icon, children }) {
  return <div className="flex items-start gap-2.5 text-sm text-zinc-300"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6A00]" />{children}</div>;
}

function PlaylistCard({ pl, onChange, onRemove }) {
  const embed = spotifyEmbed(pl.url);
  const invalid = pl.url.trim() && !embed;
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-4" data-testid={`dj-playlist-${pl.id}`}>
      <div className="flex gap-2">
        <Input
          data-testid={`dj-playlist-name-${pl.id}`}
          placeholder="Nom (ex: Cocktail, Dîner, Soirée)"
          value={pl.name}
          onChange={(e) => onChange(pl.id, "name", e.target.value)}
          className="max-w-[240px]"
        />
        <button onClick={() => onRemove(pl.id)} data-testid={`dj-playlist-del-${pl.id}`} className="ml-auto rounded-md p-2 text-zinc-500 hover:bg-red-500/15 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
      <Input
        data-testid={`dj-playlist-url-${pl.id}`}
        placeholder="Lien Spotify (playlist, album ou titre)…"
        value={pl.url}
        onChange={(e) => onChange(pl.id, "url", e.target.value)}
        className="mt-2"
      />
      {invalid && <p className="mt-2 text-sm text-red-400" data-testid={`dj-playlist-invalid-${pl.id}`}>Lien Spotify non reconnu.</p>}
      {embed && (
        <div className="mt-3 space-y-2">
          <iframe
            title={pl.name || "Spotify"}
            data-testid={`dj-playlist-player-${pl.id}`}
            src={embed}
            width="100%"
            height="352"
            style={{ border: 0, borderRadius: 12 }}
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
          />
          <a href={pl.url} target="_blank" rel="noreferrer" data-testid={`dj-playlist-open-${pl.id}`} className="inline-flex items-center gap-1.5 text-sm text-[#1DB954] hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> Ouvrir dans Spotify
          </a>
        </div>
      )}
    </div>
  );
}

function Sheet({ eventId, onBack }) {
  const [data, setData] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    api.get(`/dj/events/${eventId}`).then(({ data }) => {
      setData(data);
      let pls = data.sheet.spotify_playlists || [];
      // Backward compat: migrate a single legacy spotify_url into the list.
      if (!pls.length && data.sheet.spotify_url) pls = [{ id: uid(), name: "Playlist", url: data.sheet.spotify_url }];
      setPlaylists(pls);
      setChecklist(data.sheet.checklist || []);
      setNotes(data.sheet.notes || "");
    });
  }, [eventId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/dj/events/${eventId}/sheet`, { spotify_playlists: playlists, checklist, notes });
      toast.success("Fiche DJ enregistrée");
    } catch { toast.error("Impossible d'enregistrer la fiche"); }
    finally { setSaving(false); }
  };

  const addPlaylist = () => setPlaylists((p) => [...p, { id: uid(), name: "", url: "" }]);
  const updatePlaylist = (id, field, value) => setPlaylists((p) => p.map((pl) => (pl.id === id ? { ...pl, [field]: value } : pl)));
  const removePlaylist = (id) => setPlaylists((p) => p.filter((pl) => pl.id !== id));

  const toggleItem = (id) => setChecklist((c) => c.map((it) => (it.id === id ? { ...it, done: !it.done } : it)));
  const addItem = () => {
    if (!newItem.trim()) return;
    setChecklist((c) => [...c, { id: uid(), label: newItem.trim(), done: false }]);
    setNewItem("");
  };
  const removeItem = (id) => setChecklist((c) => c.filter((it) => it.id !== id));

  if (!data) return <p className="text-zinc-500" data-testid="dj-sheet-loading">Chargement de la fiche…</p>;
  const ev = data.event;
  const doneCount = checklist.filter((c) => c.done).length;

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="dj-sheet">
      <div className="flex items-center justify-between gap-3">
        {onBack ? (
          <button onClick={onBack} data-testid="dj-back-btn" className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
            <ChevronLeft className="h-4 w-4" /> Retour
          </button>
        ) : <span />}
        <Button onClick={save} disabled={saving} data-testid="dj-save-btn" className="rounded-full bg-[#FF6A00] hover:bg-[#FF6A00]/90">
          <Save className="mr-1.5 h-4 w-4" />{saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      {/* Event info */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="dj-event-info">
        <h1 className="font-display text-2xl font-bold text-white">{ev.title}</h1>
        <div className="mt-3 space-y-2">
          <InfoRow icon={CalIcon}>{fmtDate(ev.date)}{fmtTime(ev.date) && ` · ${fmtTime(ev.date)}`}</InfoRow>
          {ev.location && <InfoRow icon={MapPin}>{ev.location}</InfoRow>}
          {typeof ev.guests === "number" && ev.guests > 0 && <InfoRow icon={Users}>{ev.guests} invités</InfoRow>}
          {ev.contact && <InfoRow icon={Phone}>{ev.contact.name}{ev.contact.phone ? ` — ${ev.contact.phone}` : ""}</InfoRow>}
        </div>
        {ev.notes && <p className="mt-3 rounded-lg bg-zinc-950/60 p-3 text-sm text-zinc-400">{ev.notes}</p>}
      </div>

      {/* Spotify playlists */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="dj-spotify">
        <div className="mb-3 flex items-center gap-2 text-white"><ListMusic className="h-5 w-5 text-[#1DB954]" /><h2 className="font-display text-lg font-bold">Playlists Spotify</h2><span className="text-sm text-zinc-500">({playlists.length})</span></div>
        <div className="space-y-3">
          {playlists.map((pl) => (
            <PlaylistCard key={pl.id} pl={pl} onChange={updatePlaylist} onRemove={removePlaylist} />
          ))}
          {!playlists.length && <p className="text-sm text-zinc-600">Aucune playlist. Ajoutez-en autant que nécessaire (cocktail, dîner, soirée…).</p>}
        </div>
        <Button onClick={addPlaylist} data-testid="dj-playlist-add" variant="outline" className="mt-3 border-zinc-700">
          <Plus className="mr-1.5 h-4 w-4" /> Ajouter une playlist
        </Button>
      </div>

      {/* Checklist */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="dj-checklist">
        <div className="mb-3 flex items-center gap-2 text-white"><CheckSquare className="h-5 w-5 text-[#FFD500]" /><h2 className="font-display text-lg font-bold">Checklist Jour J</h2><span className="text-sm text-zinc-500">({doneCount}/{checklist.length})</span></div>
        <div className="space-y-1.5">
          {checklist.map((it) => (
            <div key={it.id} data-testid={`dj-check-${it.id}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-950/50 p-3">
              <button onClick={() => toggleItem(it.id)} data-testid={`dj-check-toggle-${it.id}`} className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors ${it.done ? "border-[#FF6A00] bg-[#FF6A00]" : "border-zinc-600"}`}>
                {it.done && <CheckSquare className="h-4 w-4 text-black" />}
              </button>
              <span className={`flex-1 text-sm ${it.done ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{it.label}</span>
              <button onClick={() => removeItem(it.id)} data-testid={`dj-check-del-${it.id}`} className="rounded-md p-1.5 text-zinc-600 hover:bg-red-500/15 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <Input data-testid="dj-check-input" placeholder="Ajouter un point de contrôle…" value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} />
          <Button onClick={addItem} data-testid="dj-check-add" variant="outline" className="border-zinc-700"><Plus className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Notes */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid="dj-notes">
        <div className="mb-3 flex items-center gap-2 text-white"><StickyNote className="h-5 w-5 text-[#FFD500]" /><h2 className="font-display text-lg font-bold">Notes</h2></div>
        <Textarea data-testid="dj-notes-input" rows={4} placeholder="Déroulé, consignes du client, contacts sur place…" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      <div className="pb-8">
        <Button onClick={save} disabled={saving} data-testid="dj-save-btn-bottom" className="w-full rounded-full bg-[#FF6A00] hover:bg-[#FF6A00]/90">
          <Save className="mr-1.5 h-4 w-4" />{saving ? "Enregistrement…" : "Enregistrer la fiche"}
        </Button>
      </div>
    </div>
  );
}

export default function DJModule({ embeddedEventId }) {
  const [selected, setSelected] = useState(embeddedEventId || null);
  return (
    <div className="space-y-6" data-testid="dj-module">
      {!embeddedEventId && !selected && (
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FF6A00]/15"><Music className="h-7 w-7 text-[#FF6A00]" strokeWidth={1.6} /></div>
          <div>
            <h1 className="font-display text-3xl font-bold text-white">DJ / Animateur</h1>
            <p className="mt-1 text-zinc-500">Fiches événement : playlists Spotify & checklist jour J.</p>
          </div>
        </div>
      )}
      {selected
        ? <Sheet eventId={selected} onBack={embeddedEventId ? null : () => setSelected(null)} />
        : <EventList onOpen={setSelected} />}
    </div>
  );
}
